
import type { ReactNode } from 'react'

import { useArtwork } from '~/api/artwork'
import { whoIs } from '~/calendar/views'
import { bubbleFor } from './bubbles'
import { Faces } from './Faces'
import { Marks } from './Marks'
import { useMarks } from './MarksContext'
import type { LandingEvent } from '~/api/types'

import s from './EventCard.module.css'

import { AppLink } from './AppLink'
import type { SeedRow } from '~/sheet/seed'

const FORMATS = new Set([
  'signing',
  'book-signing',
  'meet-greet',
  'meet-and-greet',
  'greet',
  'q-and-a',
  'dj-set',
  'after-party',
  'afterparty',
  'aftershow',
  'listening-party',
  'album-playback',
  'photo-op',
  'in-conversation',
])

/**
 * Whether this says what happens at the gig rather than who is playing.
 *
 * The twin of `artists.is_format`, and the vocabulary has to be kept the same in both
 * places — the server marks these as placeholders so they get no page, and this stops the
 * card billing one as the support. `live` is deliberately absent from both: LIVE is a band,
 * and in every title this was written for the word sits on the left of the `+`.
 *
 * The key is folded here rather than by a port of `slugify`, which does transliteration and
 * a mixed-script fallback that this cannot need: every phrase in the set is plain ASCII, so
 * a name those rules would change is a name that is not in the set either way.
 */
export function isFormat(name: string): boolean {
  const said = name.trim().replace(/\s*[[(][^\])]*[\])]\s*$/, '').trim()
  const key = said
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return key !== '' && FORMATS.has(key)
}

/**
 * The gig card, ported from `_landing_page.py`'s `_cell` and the four helpers under it.
 *
 * One card is one event, and every part of it goes to the same place: `/e/:id` from the
 * artwork, from the night, and from the name. Linking act names to `/a/:id` instead was
 * tried and reverted — a bill of four then offered four destinations and none of them was
 * the gig the reader was looking at.
 */

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
const DAYS_LONG = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]
const MONS = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
]
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/**
 * A gig starting before 05:00 belongs to the evening before. `calendar.public_landing`
 * buckets on the same rule; this is the display half of it, and the two have to agree or a
 * card lands in one section under another section's date.
 */
const NIGHT_STARTS_AT_HOUR = 5

/** `noUncheckedIndexedAccess` types every lookup as possibly absent. Both callers below
 *  index a fixed list by a value already reduced to that list's own length. */
function nameAt(names: readonly string[], index: number): string {
  return names[index] ?? ''
}

/**
 * `startsAt` is UTC and the page is about London. Everything printed on the card — the
 * hour, the day, and which night the gig counts as — is read off the London wall clock.
 */
const LONDON = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  // `h23` and not `hour12: false`: the latter renders midnight as hour 24 in some engines,
  // which is also the hour that decides which night the gig belongs to.
  hourCycle: 'h23',
})

interface Wall {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

function local(startsAt: string): Wall {
  const parts = LONDON.formatToParts(new Date(startsAt))
  const field = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find(part => part.type === type)?.value ?? 0)
  return {
    year: field('year'),
    month: field('month'),
    day: field('day'),
    hour: field('hour'),
    minute: field('minute'),
  }
}

interface Night {
  day: number
  month: number
  /** Monday is 0, as Python's `date.weekday()` has it — not JavaScript's Sunday. */
  weekday: number
}

/** The night a gig belongs to: before 05:00 London it is the evening before. */
function night(when: Wall): Night {
  const midnight = Date.UTC(when.year, when.month - 1, when.day)
  const day = new Date(
    when.hour < NIGHT_STARTS_AT_HOUR ? midnight - 24 * 60 * 60 * 1000 : midnight,
  )
  return {
    day: day.getUTCDate(),
    month: day.getUTCMonth() + 1,
    weekday: (day.getUTCDay() + 6) % 7,
  }
}

function dayLabel(on: Night, withMonth: boolean): string {
  const text = `${nameAt(DAYS, on.weekday)} ${on.day}`
  return withMonth ? `${text} ${nameAt(MONS, on.month - 1)}` : text
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/**
 * The same night, said rather than printed.
 *
 * `WED 19 AUG` and `10.30` are set as two blocks with no space between them, so a screen
 * reader ran them together as "WED 19 AUG10.30". This is the version for the label.
 */
function spoken(event: LandingEvent, when: Wall, withMonth: boolean): string {
  const on = night(when)
  let said = `${nameAt(DAYS_LONG, on.weekday)} ${on.day}`
  if (withMonth) said += ` ${nameAt(MONTHS, on.month - 1)}`
  if (!event.timeKnown) return `${said}, time to be confirmed`
  return `${said} at ${pad(when.hour)}:${pad(when.minute)}`
}

export interface Bill {
  headline: string
  more: string[]
  rest: number
}

/**
 * The headline act, then three more of the bill, then a count of what is left.
 *
 * Bills reach forty names. Printing the lot on a card this size is four lines of grey;
 * printing only the first throws away the reason somebody is going. Four names and a
 * number is the compromise.
 *
 * The number comes from `event.bill` rather than from the title, because the title stops at
 * six names now. Counting title segments would have told a reader that a day of All Points
 * East has two acts they cannot see when it has thirty-four. Whichever is larger wins: a
 * title can also say more than the bill does, and neither may be understated.
 */
export function bill(title: string, playing: number): Bill {
  const acts: string[] = []
  for (const act of title.split(' + ').map(part => part.trim())) {
    if (act === '') continue
    // `Orville Peck: Live + Signing` is one act doing two things, not a night whose second
    // act is called Signing. Put the piece back where it was cut from.
    if (acts.length > 0 && isFormat(act)) acts[acts.length - 1] += ` + ${act}`
    else acts.push(act)
  }
  const [headline, ...support] = acts
  // Fewer than two names: the whole title stands as the headline, unsplit.
  if (headline === undefined || support.length === 0) return { headline: title, more: [], rest: 0 }
  return {
    headline,
    more: support.slice(0, 3),
    rest: Math.max(0, support.length - 3, playing - 4),
  }
}

/**
 * The promoter, unless it is the room or the act said a second time.
 *
 * `Scala` at Scala, `Ata Kak` by Ata Kak — a third line that carries no new fact is worse
 * than a missing one, because it costs the same space and reads as though it does.
 */
function festival(event: LandingEvent, headline: string): string {
  const brand = (event.festival ?? '').trim()
  if (!brand) return ''
  // Python folds case with `str.casefold`, which has no JavaScript equivalent;
  // `toLowerCase` differs only on characters no promoter here has used (ß, ﬁ).
  const folded = brand.toLowerCase()
  const venue = event.venue.toLowerCase()
  if (venue.includes(folded) || folded.includes(venue)) return ''
  if (event.title.toLowerCase().includes(folded)) return ''
  if (folded.includes(headline.toLowerCase())) return ''
  return brand
}

export interface EventCardProps {
  event: LandingEvent
  /** Above the fold. Fetches the artwork at high priority instead of deferring it. */
  eager?: boolean
  /** Off inside a section already headed by a month, where "AUG" on every row is noise. */
  withMonth?: boolean
  /**
   * Off in a week column, where the day is the column heading and the card would say it
   * again — and disagree with it. A gig at 01:00 belongs to the night before under the
   * 05:00 rule, so a Monday column legitimately holds a card whose own label reads SUN 23.
   * Two dates on one card, one of them contradicting the header above it.
   *
   * The time stays. It is the fact a reader is actually looking for in a column already
   * headed by the day.
   */
  withDay?: boolean
  /** Off on the venue page: the room is the headline there, and the card would repeat it. */
  withVenue?: boolean
  /**
   * `stack` lays the card on its side for the "Freshly curated" columns, where a month is a
   * narrow column rather than a row across the page. The section used to reach in and
   * restyle the card's internals from outside; scoped class names forbid that, and a named
   * variant is the better contract anyway.
   */
  variant?: 'row' | 'stack'
  /**
   * Where the friends on it are drawn.
   *
   * `over` is the stack on the artwork's bottom-left, the standing card's own. `line` is
   * a line after the venue — faces at 16px and `whoIs`'s sentence beside them, capped at
   * three names — for a card whose artwork is narrower than three faces are wide. The
   * `stack` variant's is, so it takes the line whatever it is asked; the week's card is
   * the standing one and asks for the line by determination.
   */
  faces?: 'over' | 'line'
  /**
   * The side of one face on the artwork. One number for every standing card: at 22px a
   * face was a dot on a poster the width of a column, and the owner asked for faces he
   * could see. Bigger costs picture — three faces and a plate cover a strip of the
   * artwork's foot, more of a narrow card than a wide one — and that trade is the
   * owner's, made with the card in front of him. Only the `over` form reads it — the
   * line's faces are one size everywhere.
   */
  faceSize?: number
  /**
   * Controls laid over the artwork — the heart, the going tick, the follow star.
   *
   * The front page draws none: it is a page for reading, and every one of those posts. The
   * calendar's list draws all three. That is the only difference between the two cards, and
   * making it a slot is what let the second one be deleted: they were two components with
   * the same job, and the calendar's had already drifted to a different shape entirely.
   */
  /**
   * Anything the card should say that this row knows and a front-page row does not — the
   * price, who is going, whether it is cancelled.
   *
   * A slot rather than a shared design, because the difference is real: the front page shows
   * a curated gig to a stranger and the calendar shows every gig to its keeper. Matching the
   * card is worth doing; pretending the two have the same facts is not, and dropping the
   * price to make them identical would have been a redesign disguised as a port.
   */
  tags?: ReactNode
  /**
   * The row the sheet opens from, when it is not the one drawn. The calendar's list draws a
   * run as one card per night, each a copy titled "(Day n/N)"; the sheet is the gig, so it
   * takes the row as the server sent it. Every other caller draws the row it was sent.
   */
  seed?: SeedRow
}

export function EventCard({
  event,
  eager = false,
  withMonth = true,
  withDay = true,
  withVenue = true,
  variant = 'row',
  faces = variant === 'stack' ? 'line' : 'over',
  faceSize = 32,
  tags,
  seed = event,
}: EventCardProps) {
  // Keyed by the URL, so a card recycled onto a different gig by React does not inherit the
  // previous one's failure and hide artwork that loads perfectly well.
  const acting = useMarks()
  const { src: art, broken, onError: setBroken } = useArtwork(event.image)

  const { headline, more, rest } = bill(event.title, event.bill)
  const when = local(event.startsAt)
  const gig = `/e/${event.id}`
  // Each link below seeds the sheet with `seed`, which is this row unless the caller drew a
  // copy of it. A calendar row carries the server's `dayLong` and `time`, so its sheet opens
  // with the night line; a front-page row carries neither, and its sheet opens with the title
  // and the picture alone.
  const brand = festival(event, headline)
  const support = more.join(' + ')

  return (
    <li className={variant === 'stack' ? `${s.cell} ${s.stack}` : s.cell}>
      {/*
       * tabindex and aria-hidden together: the picture goes exactly where the name below it
       * goes, so a keyboard reaches this card's one destination once rather than three
       * times, and a screen reader is not read an unlabelled link before the act's name.
       */}
      {/*
        * The marks come from context, not from a prop.
        *
        * Every card gets them wherever it is drawn, so adding a card somewhere new does not
        * mean remembering to wire three callbacks — which is exactly what had gone wrong:
        * the front page had them, the calendar had its own inline pair that had drifted,
        * and the venue page and "just added" had none.
        */}
      {acting ? (
        <span className={s.marks}>
          <Marks
            gig={event}
            me={acting.me}
            onSave={acting.save}
            onGoing={acting.going}
            onStar={acting.star}
          />
        </span>
      ) : null}
      {/*
        * The friends on it, over the artwork's bottom-left — the other corner from the
        * marks. A sibling of `.shot`, not a child: the artwork's link is `aria-hidden` and
        * skipped by the keyboard, and an anchor inside an anchor is markup no browser
        * agrees about. Absolute against `.cell`, the way the marks are.
        *
        * The `over` form only. A card asked for the line carries them under the venue
        * instead — never both: one stack per card, whichever form it takes.
        */}
      {faces === 'over' && event.friends?.length ? (
        <span className={s.facesbox}>
          <Faces
            className={s.faces}
            friends={event.friends}
            size={faceSize}
            past={event.past ?? false}
            to={gig}
          />
        </span>
      ) : null}
      <AppLink
        className={s.shot}
        to={gig}
        seed={seed}
        tabIndex={-1}
        aria-hidden="true"
        title={event.title}
      >
        {art && !broken ? (
          <span className={s.art}>
            <img
              src={art}
              alt=""
              loading={eager ? undefined : 'lazy'}
              fetchPriority={eager ? 'high' : undefined}
              decoding="async"
              // Promoter art is hosted elsewhere and sometimes 404s. The Python page caught
              // this with one capture-phase `error` listener on the document that swapped
              // the plate for the "no picture" one; without it a dead image is the browser's
              // broken-file glyph inside a 1:1 bordered box. Four of the Songkick images are
              // 135-byte blanks served with a 200, so this is not hypothetical.
              onError={setBroken}
            />
          </span>
        ) : (
          /*
           * A plate rather than the words "no picture", which is what this was: honest, and
           * a page of forty of them read as a page of things that are missing. The bubble is
           * chosen from the gig's own id, so it is the same one here, in the calendar, and
           * in the sheet — a card that reshuffled its face on every render would be worse
           * than the box it replaced.
           *
           * Still `art-none`, because the class says which branch this is — `.art` is the
           * shared box and this is the one with no photograph behind it. It is graded and
           * scrimmed like any other picture: a row where the pictureless cards are the
           * brightest thing on it makes the gigs we know least about the loudest.
           */
          <span className={`${s.art} ${s['art-none']}`}>
            <img src={bubbleFor(event.id)} alt="" loading="lazy" decoding="async" />
          </span>
        )}
      </AppLink>

      {/*
       * The link to the gig itself, and the part a reader is already reading to decide
       * whether to click. The label is `_spoken`, because the day and the time are two
       * blocks with no space between them and a screen reader ran them together as
       * "WED 19 AUG10.30".
       */}
      <AppLink
        className={`${s.when} ${s.mono}`}
        to={gig}
        seed={seed}
        aria-label={`${event.title}, ${spoken(event, when, withMonth)}`}
      >
        {/* A run says its span. Everything else derives one night from the clock, which is
            right for every gig and wrong for the only two things here that last four. */}
        {withDay ? (
          <span className={s.dt}>{event.dayRange || dayLabel(night(when), withMonth)}</span>
        ) : null}
        {event.timeKnown ? (
          <span className={s.tm}>
            {pad(when.hour)}.{pad(when.minute)}
          </span>
        ) : (
          <span className={`${s.tm} ${s.tbc}`}>TIME TBC</span>
        )}
      </AppLink>

      {/* Not a link to the artist. A card is one event. */}
      <AppLink className={s.name} to={gig} seed={seed}>
        {headline}
      </AppLink>

      {support !== '' || rest > 0 ? (
        <span className={s.sup}>
          {support}
          {rest > 0 ? <span className={s.andmore}> + {rest} more</span> : null}
        </span>
      ) : null}

      {withVenue && event.venueSlug !== null ? (
        <AppLink className={s.venue} to={`/v/${event.venueSlug}`}>
          {event.venue}
        </AppLink>
      ) : null}
      {/* A placeholder room — `TBA`, `Secret Location` — is still what the listing says, so
          the name stays. It has no page, so it is not a link to one. */}
      {withVenue && event.venueSlug === null ? (
        <span className={s.venue}>{event.venue}</span>
      ) : null}

      {/*
        * The line form: the faces and the sentence, one link, after the venue.
        *
        * No `+N` plate. The words are `whoIs` at the same cap as the faces, so every face
        * drawn has its name beside it and the rest are the sentence's own "and N more" — a
        * pill saying the same number again would be a count beside a face, which is what
        * the compact row was cleared of.
        */}
      {faces === 'line' && event.friends?.length ? (
        <Faces
          className={s.line}
          friends={event.friends}
          size={16}
          past={event.past ?? false}
          to={gig}
          plate={false}
        >
          {whoIs(event.friends, { cap: 3, past: event.past ?? false })}
        </Faces>
      ) : null}

      {brand !== '' ? <span className={`${s.fest} ${s.mono}`}>{brand}</span> : null}

      {tags ? <span className={s.tags}>{tags}</span> : null}
    </li>
  )
}
